<script>
import { VueDraggable } from "vue-draggable-plus";
import { useLandingPageStore} from "../../../stores/landingPage";
import { useAnalyticEventStore } from "../../../stores/analyticEvents";
import { useOrganizationStore } from "../../../stores/organizations";
import BaseModal from "../../components/BaseModal.vue";
import PageArticle from "../../components/landing/pageArticle.vue";
import Icon from "../../components/Icon.vue";

export default {
  name: "LayoutManager",
  components: {PageArticle, BaseModal, VueDraggable, Icon},
  emits: ['cancel'],
  setup() {
    const landingPageStore = useLandingPageStore();
    const analyticEventStore = useAnalyticEventStore();
    const organizationStore = useOrganizationStore();
    const deleteConfirmation = false;

    return {
      landingPageStore,
      analyticEventStore,
      organizationStore,
      deleteConfirmation
    };
  },
  data() {
    return {
      headerFilter: [],
      footerFilter: [],
      staticFilter: [],
      headerComponents: [],
      components: [],
      footerComponents: [],
      originalOrder: [],
      componentToDelete: null,
      pendingDeletions: [],
    };
  },
  watch: {
    'landingPageStore.draftLayout.pages': {
      handler() {
        this.cloneComponents();
      },
      deep: true
    },
  },
  mounted() {
    this.cloneComponents();
    this.orderComponents();
  },
  methods: {
    /**
     * pass in an array of component types to filter by
     * pass in a boolean to include or exclude the component types
     * example: filterComponents(['header', 'hero_banner'], false) will return all components except header and hero_banner
     * filterArray: Type: Array
     * include: Type: Boolean
     * @returns Array
     */
    filterComponents(filterArray, include) {
      const landingPage = this.landingPageStore.draftLayout?.pages?.find((page) => page.page_type === 'landing_page') || {};
      const components = landingPage?.components.filter(c => c.archived !== true) || [];

      if (include){
        return components.filter(component => filterArray.includes(component.component_type));
      } else {
        return components.filter(component => !filterArray.includes(component.component_type));
      }
    },
    cloneComponents() {
      // Fetch only components that can be re-ordered
      const landingPage = this.landingPageStore.draftLayout?.pages?.find((page) => page.page_type === 'landing_page') || {};
      const allComponents = landingPage?.components.filter(c => c.archived !== true) || [];

      this.headerComponents = this.filterComponents(['header', 'hero_banner'], true);
      this.footerComponents = this.filterComponents(['footer'], true);
      this.components = allComponents.filter(c =>
        !['header','hero_banner','footer'].includes(c.component_type)
      );

      // Take a snapshot of the original order so we can restore it if the user decides to cancel
      this.originalOrder = JSON.parse(JSON.stringify(this.filterComponents(this.staticFilter, false)));
    },
    /**
     * utilize our snapshot to return components to its original state
     * this triggers a watch event which will update the landing page store
     */
    cancel() {
      this.components = JSON.parse(JSON.stringify(this.originalOrder));

      // revert the landing page store components to the original order
      this.landingPageStore.draftLayout.pages.find((page) => page.page_type === 'landing_page').components = [
        ...this.components,
      ];

      // this.landingPageStore.updateComponentOrder(this.components);
      this.$emit("close");
    },

    save() {

      // Insert header and footer components
      const merged = [ ...this.headerComponents, ...this.components, ...this.footerComponents ];

      // Update the component order based on index
      merged.forEach((c, idx) => { c.order = idx; });
      this.landingPageStore.normalizeLandingPageComponents();

      // Update the draft
      this.landingPageStore.dirtyDraft = true;
      const landingPage = this.landingPageStore.draftLayout?.pages?.find((page) => page.page_type === 'landing_page');
      if (landingPage) { landingPage.components = merged; }
      this.analyticEventStore.setOrganizationId(this.organizationStore.organizationId);
      this.analyticEventStore.logEvent({ action: 'builder-reorder-components' });
      this.$emit("close");
      this.landingPageStore.saveCurrentDraft();
    },
    orderComponents() {
      this.components.sort((a, b) => a.order - b.order);
    },
    componentHeaderTitle(type, component) {
      const typeConfig = {
        site_settings: { label: 'Branding' },
        header: { label: 'Header' },
        hero_banner: { label: 'Page Banner' },
        donatable_grid: { label: 'Giving Section' },
        footer: { label: 'Footer' },
        article: { label: 'Content Section', usesTitle: true },
        video: { label: 'Video', usesTitle: true }
      };

      const config = typeConfig[type];
      if (!config) return type;
      if (!config.usesTitle) return config.label;

      const title = component.component_data?.title?.value ||
        (typeof component.component_data?.title === 'string' ? component.component_data.title : '');

      if (title?.trim()) return title;

      if (component.sequence_number != null) return `${config.label} #${component.sequence_number}`;

      return `${config.label}`;
    },
    // Maps icon type to the name of the icon
    componentIcon(type) {
      const iconMap = {
        "site_settings": "gear",
        "header": "edit-pencil",
        "hero_banner": "image-solid-full",
        "donatable_grid": "hand-heart",
        "article": "book-solid-full",
        "video": "video-solid-full",
        "footer": "list-solid-full",
      };
      return iconMap[type] || "document";
    },
    // archive() {
    //   const component = this.componentToDelete;
    //   // this.pendingDeletions.push(component);
    //   component.archived = true;
    //   this.landingPageStore.archiveComponentInDraft(component);
    //   this.components = this.components.filter(
    //     c => c.id !== component.id
    //   );
    //   this.componentToDelete = null;
    // },
    // deleteComponentConfirmation(component){
    //   this.deleteConfirmation = true;
    //   this.componentToDelete = component;
    // }
  }
};
</script>

<template>
  <div class="w-full text-center">
    <div class="subtitle-regular">
      Drag and drop to reorder sections
    </div>
  </div>
  <div>
    <div
      v-for="(component) in headerComponents"
      :key="component.id"
      class="draggable-card card drag-disabled"
    >
      <div class="icon-spacer" />
      <icon
        :icon="componentIcon(component.component_type)"
        size="18px"
        color="#555"
        class="component-icon"
      />
      <!--    Header/Page banner Titles -->
      <div class="subtitle-regular component-title">
        {{ componentHeaderTitle(component.component_type) }}
      </div>
    </div>
    <!--    Drag component happens at the icon only  -->
    <vue-draggable
      v-model="components"
      animation="150"
      handle=".icon-grab"
    >
      <div
        v-for="(component) in components"
        :key="component.id"
        class="draggable-card"
      >
        <Icon
          icon="grip-vertical"
          class="icon-grab cursor-move"
          :size="'18px'"
          :color="'#9ca3af'"
        />
        <icon
          :icon="componentIcon(component.component_type)"
          size="18px"
          color="#555"
          class="component-icon"
        />
        <!--   Draggable Titles -->
        <div class="subtitle-regular component-title">
          {{ componentHeaderTitle(component.component_type, component) }}
        </div>
      </div>
    </vue-draggable>
    <div
      v-for="(component) in footerComponents"
      :key="component.id"
      class="draggable-card card drag-disabled"
    >
      <div class="icon-spacer" />
      <icon
        :icon="componentIcon(component.component_type)"
        size="18px"
        color="#555"
        class="component-icon"
      />
      <div class="subtitle-regular component-title">
        {{ componentHeaderTitle(component.component_type) }}
      </div>
    </div>
    <div />
    <div class="flex flex-col gap-2 w-full my-4">
      <div class="flex flex-row gap-2 w-full my-2">
        <button
          class="btn btn-filled btn-secondary flex justify-center w-full"
          @click="save"
        >
          Done
        </button>
        <button
          class="btn btn-filled btn-secondary flex justify-center w-full"
          @click="cancel"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.draggable-card {
  @apply rounded-md w-full my-2 border border-gray-100 text-base font-semibold py-3 px-4 relative shadow-md;
  cursor: default;
  display: flex;
  align-items: center;
  gap: 8px;
}

.drag-disabled {
  @apply cursor-not-allowed opacity-70 bg-gray-100;
  filter: grayscale(0.5);
}
.component-title{
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: left;
}
.icon-grab {
  flex-shrink: 0;
  cursor: move;
}
.component-icon {
  flex-shrink: 0;
}
.icon-spacer {
  width: 18px;
  flex-shrink: 0;
}

.not-draggable{
  margin-left: 26px;
}

</style>
